import { call, put, select, takeLatest } from "redux-saga/effects";
import { generatePath } from "react-router-dom";
import BackendClient from "api/backend-client";
import { RecipeScalingApiMethods } from "api/api-methods";
import WSService from "api/web-socket.service";
import { InvokeRsSocketMethods } from "api/rs-socket-callbacks";
import RoutePaths from "routes/route-paths";
import logger from "utils/logger";
import { isNotEmpty } from "utils/validate";
import { DialogStatus, GlobalEntityType } from "shared/types";
import { waitForLogin } from "../auth/auth.saga";
import { addDynamicFailure, addDynamicSuccessful, addPopulationEffectFailure, addPopulationEffectSuccessful, addPopulationFailure, addPopulationSuccessful, addVariableDependencyFailure, addVariableDependencySuccessful, addVariableEffectFailure, addVariableEffectSuccessful, addVariableFailure, addVariableSuccessful, CellModelActionType, changeRefinementRunStateFailure, changeRefinementRunStateSuccessful, createRefinedCellModelFailure, deleteDynamicFailure, deleteDynamicSuccessful, deletePopulationEffectFailure, deletePopulationEffectSuccessful, deletePopulationFailure, deletePopulationSuccessful, deleteVariableDependencyFailure, deleteVariableDependencySuccessful, deleteVariableEffectFailure, deleteVariableEffectSuccessful, deleteVariableFailure, deleteVariableSuccessful, editDynamicFailure, editDynamicSuccessful, editPopulationFailure, editPopulationSuccessful, getRefinementParametersFailure, getRefinementParametersSuccessful, loadCellModelDynamicNamesFailure, loadCellModelDynamicNamesSuccessful, loadCellModelFailure, loadCellModelPopulationNamesFailure, loadCellModelPopulationNamesSuccessful, loadCellModelSuccessful, loadCellModelVariables, loadCellModelVariablesFailure, loadCellModelVariablesSuccessful, setCreateRefinedCellModelDialogStatus, setCurrentRefinementTab, setDependencyDialogStatus, setDisplayMode, setDynamicDialogStatus, setEditingPopulation, setPopulationEffectDialogStatus, setRefineDialogStatus, setRefinementData, setRefinementValidationErrors, setVariableDependencyGraphData, setVariableEffectDialogStatus, updatePopulationEffectFailure, updatePopulationEffectSuccessful, updateVariableDependencyFailure, updateVariableDependencySuccessful, updateVariableEffectFailure, updateVariableEffectSuccessful, validateRefinementFailure, } from "./cell-model.actions";
import { checkIsRefinementFailed, checkRefinementIsInProgress, handleShowRefinementNotifications, REFINEMENT_STARTED_LOCAL_STORAGE_ITEM, } from "./cell-model.utils";
import { CellModelDisplayMode, ChangeRefinementRunStateAction, } from "./cell-model.types";
import { loadProcessVariables } from "../global/global.actions";
import { toggleNoSeatsToastVisible } from "../licensing/licensing.actions";
import { currentRefinementTabSelector, displayModeSelector } from "./cell-model.selectors";
import { makeUnsaved, setCurrentRequestTemplate, setDeleteTarget, setServerError, setupKnowledgeBaseItem, } from "../knowledge-base-item/knowledge-base-item.actions";
import { connectChannelSaga, loadItemErrorHandler } from "../knowledge-base-item/knowledge-base-item.saga";
import { RemovableEntityType, RequestTemplate, } from "../knowledge-base-item/knowledge-base-item.types";
import { apiErrorHandler, checkConnectionSaga } from "store/global/global.saga";
import { currentItemIdentifierSelector, deleteTargetSelector, isEditableSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { noSeatsAvailableSelector } from "store/licensing/licensing.selectors";
import { waitForLicensingData } from "store/licensing/licensing.saga";
function* loadCellModelSaga({ payload }) {
    try {
        yield call(checkConnectionSaga);
        yield call(waitForLogin);
        yield put(loadCellModelVariables());
        yield put(loadProcessVariables());
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetCellModel], payload);
        const { name, description, lastSavedDate, createdAt, createdBy, lastSavedBy, tags, state, hasUnsavedChanges, isLocked, isCompleted, refinement, refined, owners, } = data;
        yield put(loadCellModelSuccessful({ state }));
        yield put(setupKnowledgeBaseItem({
            id: payload,
            name,
            description,
            lastSavedDate,
            createdAt,
            createdBy,
            lastSavedBy,
            tags,
            hasUnsavedChanges,
            entityType: GlobalEntityType.CellModel,
            isLocked,
            isCompleted,
            owners,
        }));
        yield put(setRefinementData({
            cellModelId: payload,
            refined,
            refinement,
            withoutToastError: true,
        }));
        yield call(waitForLicensingData);
        const noSeatsAvailable = yield select(noSeatsAvailableSelector);
        if (noSeatsAvailable) {
            const editable = yield select(isEditableSelector);
            if (editable) {
                yield put(toggleNoSeatsToastVisible(true));
            }
        }
        else {
            yield call(connectChannelSaga);
        }
    }
    catch (e) {
        yield call(loadItemErrorHandler, e);
        yield call(apiErrorHandler, e, "cell model load failed");
        yield put(loadCellModelFailure());
    }
}
export function* recovery(id) {
    yield call(waitForLogin);
    const backendClient = BackendClient.getInstance();
    const state = yield call([backendClient, RecipeScalingApiMethods.GetCellModelTemporary], id);
    yield put(loadCellModelSuccessful({ state: state || null }));
}
function* addCellModelPopulationSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.POPULATION_POST));
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.AddPopulation], payload);
        yield put(addPopulationSuccessful(data));
        yield put(setServerError(null));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model add population failed");
        yield put(addPopulationFailure());
    }
}
function* addCellModelDynamicSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.DYNAMIC_POST));
        yield put(setDynamicDialogStatus(DialogStatus.PENDING));
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.AddDynamic], payload);
        yield put(addDynamicSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model add dynamic failed");
        yield put(setDynamicDialogStatus(DialogStatus.OPENED));
        yield put(addDynamicFailure());
    }
}
function* loadCellModelVariablesSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([
            backendClient,
            RecipeScalingApiMethods.GetCellModelVariables,
        ]);
        yield put(loadCellModelVariablesSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model load variables failed");
        yield put(loadCellModelVariablesFailure());
    }
}
function* loadCellModelDynamicNamesSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetCellModelDynamicNames]);
        yield put(loadCellModelDynamicNamesSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model load dynamic names failed");
        yield put(loadCellModelDynamicNamesFailure());
    }
}
function* loadCellModelPopulationNamesSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetCellModelPopulationNames]);
        yield put(loadCellModelPopulationNamesSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model load population names failed");
        yield put(loadCellModelPopulationNamesFailure());
    }
}
function* editCellModelDynamicSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.DYNAMIC_PUT_DELETE));
        yield put(setDynamicDialogStatus(DialogStatus.PENDING));
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.EditDynamic], payload);
        yield put(editDynamicSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model edit dynamic failed");
        yield put(setDynamicDialogStatus(DialogStatus.OPENED));
        yield put(editDynamicFailure());
    }
}
function* deleteCellModelDynamicSaga({ payload: populationId }) {
    try {
        yield call(waitForLogin);
        const { id: dynamicId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.DYNAMIC) {
            return;
        }
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.DYNAMIC_PUT_DELETE));
        const { id: cellModelId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.DeleteDynamic], {
            cellModelId,
            populationId,
            dynamicId,
        });
        yield put(deleteDynamicSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model delete dynamic failed");
        yield put(deleteDynamicFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* editCellModelPopulationSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.POPULATION_PUT_DELETE));
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.EditPopulation], payload);
        yield put(editPopulationSuccessful(data));
        yield put(setServerError(null));
        yield put(setEditingPopulation(null));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model edit population failed");
        yield put(editPopulationFailure());
    }
}
function* deleteCellModelPopulationSaga() {
    try {
        yield call(waitForLogin);
        const { id: populationId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.POPULATION) {
            return;
        }
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.POPULATION_PUT_DELETE));
        const { id: cellModelId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.DeletePopulation], { cellModelId, populationId });
        yield put(deletePopulationSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model delete population failed");
        yield put(deletePopulationFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* addCellModelVariableSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.AddVariable], payload);
        yield put(addVariableSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model add variable failed");
        yield put(addVariableFailure());
    }
}
function* deleteCellModelVariableSaga() {
    try {
        yield call(waitForLogin);
        const { id: variableId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.VARIABLE) {
            return;
        }
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.VARIABLE_DELETE));
        const { id: cellModelId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.DeleteVariable], {
            cellModelId,
            variableId,
        });
        yield put(deleteVariableSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model delete variable failed");
        yield put(deleteVariableFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* addCellModelPopulationEffectSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.POPULATION_EFFECTS_POST));
        yield put(setPopulationEffectDialogStatus(DialogStatus.PENDING));
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.AddPopulationEffect], payload);
        yield put(addPopulationEffectSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model add population effect failed");
        yield put(setPopulationEffectDialogStatus(DialogStatus.OPENED));
        yield put(addPopulationEffectFailure());
    }
}
function* updateCellModelPopulationEffectSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.POPULATION_EFFECTS_PUT));
        yield put(setPopulationEffectDialogStatus(DialogStatus.PENDING));
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.UpdatePopulationEffect], payload);
        yield put(updatePopulationEffectSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model edit population effect failed");
        yield put(setPopulationEffectDialogStatus(DialogStatus.OPENED));
        yield put(updatePopulationEffectFailure());
    }
}
function* deleteCellModelPopulationEffectSaga({ payload: populationId }) {
    try {
        yield call(waitForLogin);
        const { id: effectId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.POPULATION_EFFECT) {
            return;
        }
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.POPULATION_EFFECTS_DELETE));
        const { id: cellModelId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.DeletePopulationEffect], {
            cellModelId,
            populationId,
            effectId,
        });
        yield put(deletePopulationEffectSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model delete population effect failed");
        yield put(deletePopulationEffectFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* addCellModelVariableDependencySaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.VARIABLE_DEPENDENCIES_POST));
        yield put(setDependencyDialogStatus(DialogStatus.PENDING));
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.AddVariableDependency], payload);
        yield put(addVariableDependencySuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model add variable dependency failed");
        yield put(setDependencyDialogStatus(DialogStatus.OPENED));
        yield put(addVariableDependencyFailure());
    }
}
function* updateCellModelVariableDependencySaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.VARIABLE_DEPENDENCIES_PUT));
        yield put(setDependencyDialogStatus(DialogStatus.PENDING));
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.UpdateVariableDependency], payload);
        yield put(updateVariableDependencySuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model edit variable dependency failed");
        yield put(setDependencyDialogStatus(DialogStatus.OPENED));
        yield put(updateVariableDependencyFailure());
    }
}
function* deleteCellModelVariableDependencySaga({ payload: dynamicId }) {
    try {
        yield call(waitForLogin);
        const { id: dependencyId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.DEPENDENCY) {
            return;
        }
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.VARIABLE_DEPENDENCIES_DELETE));
        const { id: cellModelId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.DeleteVariableDependency], {
            cellModelId,
            dynamicId,
            dependencyId,
        });
        yield put(deleteVariableDependencySuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model delete variable dependency failed");
        yield put(deleteVariableDependencyFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* getVariableDependencyGraphDataSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetVariableDependencyGraphData], payload);
        yield put(setVariableDependencyGraphData(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model get variable dependency graph data failed");
        yield put(setCurrentRequestTemplate(RequestTemplate.VARIABLE_DEPENDENCIES_GRAPH_GET));
        yield put(setVariableDependencyGraphData(null));
    }
}
function* getRefinementParametersSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetRefinementParameters], payload);
        yield put(getRefinementParametersSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model get refinement parameters failed");
        yield put(getRefinementParametersFailure());
    }
}
function* createRefinedCellModelSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const noSeatsAvailable = yield select(noSeatsAvailableSelector);
        if (noSeatsAvailable) {
            yield put(toggleNoSeatsToastVisible(true));
            return;
        }
        yield put(setCurrentRequestTemplate(RequestTemplate.REFINED_CELL_MODEL_POST));
        yield put(setRefineDialogStatus(DialogStatus.PENDING));
        yield put(setCreateRefinedCellModelDialogStatus(DialogStatus.PENDING));
        const backendClient = BackendClient.getInstance();
        const id = yield call([backendClient, RecipeScalingApiMethods.CreateRefinedCellModel], payload);
        const path = generatePath(RoutePaths.EditCellModel, { id });
        localStorage.setItem(REFINEMENT_STARTED_LOCAL_STORAGE_ITEM, String(id));
        window.open(path, "_blank");
        yield put(setRefineDialogStatus(DialogStatus.CLOSED));
        yield put(setCreateRefinedCellModelDialogStatus(DialogStatus.CLOSED));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "create refined cell model failed");
        yield put(setRefineDialogStatus(DialogStatus.OPENED));
        yield put(setCreateRefinedCellModelDialogStatus(DialogStatus.OPENED));
        yield put(createRefinedCellModelFailure());
    }
}
function* setRefinementDataHandlerSaga({ payload }) {
    try {
        handleShowRefinementNotifications(payload);
        const displayMode = yield select(displayModeSelector);
        if (!displayMode) {
            yield put(setDisplayMode(CellModelDisplayMode.PREVIEW));
        }
        const { refinement } = payload;
        const currentRefinementTab = yield select(currentRefinementTabSelector);
        if (!isNotEmpty(currentRefinementTab) && !checkRefinementIsInProgress(refinement?.status) && refinement) {
            yield put(setCurrentRefinementTab(refinement.configurations.length - 1));
        }
    }
    catch {
        logger.error("set refinement calculation data failed");
    }
}
function* changeRefinementRunStateSaga({ payload: { payload, callback }, }) {
    const { action, cellModelId } = payload;
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        switch (action) {
            case ChangeRefinementRunStateAction.STOP:
            case ChangeRefinementRunStateAction.PAUSE:
                yield call(invokeIgnoreRefineResultSaga, cellModelId);
                break;
            case ChangeRefinementRunStateAction.RESTART:
            case ChangeRefinementRunStateAction.RESUME:
                yield call(invokeWaitRefineResultSaga, {
                    payload: cellModelId,
                    type: CellModelActionType.INVOKE_WAIT_REFINE_RESULT,
                });
                break;
        }
        const status = yield call([backendClient, RecipeScalingApiMethods.ChangeRefinementRunState], payload);
        if (checkIsRefinementFailed(status)) {
            yield call(invokeIgnoreRefineResultSaga, cellModelId);
        }
        yield put(changeRefinementRunStateSuccessful(status));
        callback && callback();
    }
    catch (e) {
        yield call(apiErrorHandler, e, `${action} cell model refinement failed`);
        yield put(changeRefinementRunStateFailure());
    }
}
function* validateRefinementSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        let validationResult;
        if (payload.processDataConfigurations.length > 0) {
            validationResult = yield call([backendClient, RecipeScalingApiMethods.ValidateRefinement], payload);
        }
        const hasErrors = validationResult?.refinementProcessDataValidationResults?.some(({ ruleBreaks }) => ruleBreaks.length);
        yield put(setRefinementValidationErrors(hasErrors ? validationResult : undefined));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "Validate cell model refinement failed");
        yield put(validateRefinementFailure());
    }
}
function* invokeWaitRefineResultSaga({ payload }) {
    try {
        yield call([WSService.getInstance(), "rsInvoke"], InvokeRsSocketMethods.WAIT_REFINE_RESULT, undefined, {
            cellId: payload,
        });
    }
    catch (e) {
        logger.error("invoke wait refine result failed");
    }
}
function* invokeIgnoreRefineResultSaga(cellId) {
    try {
        yield call([WSService.getInstance(), "rsInvoke"], InvokeRsSocketMethods.IGNORE_REFINE_RESULT, undefined, {
            cellId,
        });
    }
    catch (e) {
        logger.error("invoke ignore refine result failed");
    }
}
function* addCellModelVariableEffectSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.VARIABLE_EFFECTS_POST));
        yield put(setVariableEffectDialogStatus(DialogStatus.PENDING));
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.AddEffectOnVariable], payload);
        yield put(addVariableEffectSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model add variable effect failed");
        yield put(setVariableEffectDialogStatus(DialogStatus.OPENED));
        yield put(addVariableEffectFailure());
    }
}
function* updateCellModelVariableEffectSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.VARIABLE_EFFECTS_PUT));
        yield put(setVariableEffectDialogStatus(DialogStatus.PENDING));
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.UpdateEffectOnVariable], payload);
        yield put(updateVariableEffectSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model edit variable effect failed");
        yield put(setVariableEffectDialogStatus(DialogStatus.OPENED));
        yield put(updateVariableEffectFailure());
    }
}
function* deleteCellModelVariableEffectSaga({ payload: dynamicId }) {
    try {
        yield call(waitForLogin);
        const { id: effectId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.VARIABLE_EFFECT) {
            return;
        }
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.VARIABLE_EFFECTS_DELETE));
        const { id: cellModelId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.DeleteEffectOnVariable], {
            cellModelId,
            dynamicId,
            effectId,
        });
        yield put(deleteVariableEffectSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cell model delete variable effect failed");
        yield put(deleteVariableEffectFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
export default function* cellModelSaga() {
    yield takeLatest(CellModelActionType.LOAD, loadCellModelSaga);
    yield takeLatest(CellModelActionType.LOAD_VARIABLES, loadCellModelVariablesSaga);
    yield takeLatest(CellModelActionType.LOAD_POPULATION_NAMES, loadCellModelPopulationNamesSaga);
    yield takeLatest(CellModelActionType.LOAD_DYNAMIC_NAMES, loadCellModelDynamicNamesSaga);
    yield takeLatest(CellModelActionType.ADD_POPULATION, addCellModelPopulationSaga);
    yield takeLatest(CellModelActionType.EDIT_POPULATION, editCellModelPopulationSaga);
    yield takeLatest(CellModelActionType.DELETE_POPULATION, deleteCellModelPopulationSaga);
    yield takeLatest(CellModelActionType.ADD_DYNAMIC, addCellModelDynamicSaga);
    yield takeLatest(CellModelActionType.EDIT_DYNAMIC, editCellModelDynamicSaga);
    yield takeLatest(CellModelActionType.DELETE_DYNAMIC, deleteCellModelDynamicSaga);
    yield takeLatest(CellModelActionType.ADD_VARIABLE, addCellModelVariableSaga);
    yield takeLatest(CellModelActionType.DELETE_VARIABLE, deleteCellModelVariableSaga);
    yield takeLatest(CellModelActionType.ADD_POPULATION_EFFECT, addCellModelPopulationEffectSaga);
    yield takeLatest(CellModelActionType.UPDATE_POPULATION_EFFECT, updateCellModelPopulationEffectSaga);
    yield takeLatest(CellModelActionType.DELETE_POPULATION_EFFECT, deleteCellModelPopulationEffectSaga);
    yield takeLatest(CellModelActionType.ADD_VARIABLE_DEPENDENCY, addCellModelVariableDependencySaga);
    yield takeLatest(CellModelActionType.UPDATE_VARIABLE_DEPENDENCY, updateCellModelVariableDependencySaga);
    yield takeLatest(CellModelActionType.DELETE_VARIABLE_DEPENDENCY, deleteCellModelVariableDependencySaga);
    yield takeLatest(CellModelActionType.ADD_VARIABLE_EFFECT, addCellModelVariableEffectSaga);
    yield takeLatest(CellModelActionType.UPDATE_VARIABLE_EFFECT, updateCellModelVariableEffectSaga);
    yield takeLatest(CellModelActionType.DELETE_VARIABLE_EFFECT, deleteCellModelVariableEffectSaga);
    yield takeLatest(CellModelActionType.GET_VARIABLE_DEPENDENCY_GRAPH_DATA, getVariableDependencyGraphDataSaga);
    yield takeLatest(CellModelActionType.GET_REFINEMENT_PARAMETERS, getRefinementParametersSaga);
    yield takeLatest(CellModelActionType.SET_REFINEMENT_DATA, setRefinementDataHandlerSaga);
    yield takeLatest(CellModelActionType.INVOKE_WAIT_REFINE_RESULT, invokeWaitRefineResultSaga);
    yield takeLatest(CellModelActionType.CREATE_REFINED_CELL_MODEL, createRefinedCellModelSaga);
    yield takeLatest(CellModelActionType.CHANGE_REFINEMENT_RUN_STATE, changeRefinementRunStateSaga);
    yield takeLatest(CellModelActionType.VALIDATE_REFINEMENT, validateRefinementSaga);
}
